import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserPlus, Shield, Eye, Edit3, MoreVertical, Trash2, Crown, AlertTriangle } from 'lucide-react';
import { Button, Modal } from '../common';
import { useProjectStore } from '../../stores/projectStore';
import { useUserStore } from '../../stores/userStore';
import { useAuthStore } from '../../stores/authStore';
import { showToast } from '../common/Toast';
import type { ProjectMember, ProjectRole } from '../../types';

interface ProjectMembersListProps {
  projectId: string;
  isOwner: boolean;
}

/**
 * Displays and manages project members with role-based permissions.
 * Shows member list, allows adding/removing members, and role changes.
 * Project owners and org admins can manage project members.
 */
export const ProjectMembersList: React.FC<ProjectMembersListProps> = ({ projectId, isOwner }) => {
  const { t } = useTranslation();
  const {
    projectMembers,
    memberSummary,
    fetchProjectMembers,
    fetchMemberSummary,
    addProjectMember,
    updateMemberRole,
    removeProjectMember,
    isLoading,
  } = useProjectStore();
  const { users, fetchUsers } = useUserStore();
  const { user } = useAuthStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<ProjectRole>('MEMBER');
  const [memberMenuOpen, setMemberMenuOpen] = useState<string | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState<ProjectMember | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Project owners and org admins can manage project members
  const isOrgAdmin = user?.organizationRoles?.find(r => r.orgId === user.activeOrganizationId)?.role === 'ADMIN';
  const canManage = isOwner || isOrgAdmin;

  useEffect(() => {
    fetchProjectMembers(projectId);
    fetchMemberSummary(projectId);
    fetchUsers();
  }, [projectId, fetchProjectMembers, fetchMemberSummary, fetchUsers]);

  const handleAddMember = async () => {
    if (!selectedUserId) {
      showToast(t('projects.members.selectUser'), 'error');
      return;
    }

    setActionError(null);
    const member = await addProjectMember(projectId, selectedUserId, selectedRole);
    if (member) {
      showToast(t('projects.members.memberAdded'), 'success');
      setShowAddModal(false);
      setSelectedUserId('');
      setSelectedRole('MEMBER');
    } else {
      setActionError('Failed to add member. Only project owners or admins can add members.');
    }
  };

  const handleRoleChange = async (userId: string, newRole: ProjectRole) => {
    setActionError(null);
    const result = await updateMemberRole(projectId, userId, newRole);
    if (result !== false) {
      showToast(t('projects.members.roleUpdated'), 'success');
    } else {
      setActionError('Failed to update role. Only project owners or admins can change roles.');
    }
    setMemberMenuOpen(null);
  };

  const handleRemoveMember = async () => {
    if (!showRemoveModal) return;

    setActionError(null);
    const success = await removeProjectMember(projectId, showRemoveModal.userId);
    if (success) {
      showToast(t('projects.members.memberRemoved'), 'success');
    } else {
      setActionError('Failed to remove member. Only project owners or admins can remove members.');
    }
    setShowRemoveModal(null);
  };

  const getRoleIcon = (role: ProjectRole) => {
    switch (role) {
      case 'ADMIN':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'MEMBER':
        return <Edit3 className="w-4 h-4 text-blue-500" />;
      case 'VIEWER':
        return <Eye className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleBadge = (role: ProjectRole) => {
    const colors = {
      ADMIN: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      MEMBER: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      VIEWER: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[role]}`}>
        {getRoleIcon(role)}
        {role.charAt(0) + role.slice(1).toLowerCase()}
      </span>
    );
  };

  const availableUsers = users.filter(
    (u) => !projectMembers.some((m) => m.userId === u.id)
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Members
          </h3>
          {memberSummary && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({memberSummary.totalMembers})
            </span>
          )}
        </div>
        {/* Project owners and org admins can add members */}
        {canManage && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1"
          >
            <UserPlus className="w-4 h-4" />
            {t('projects.members.addMember')}
          </Button>
        )}
      </div>

      {/* Show action error (permission denied, etc.) */}
      {actionError && (
        <div className="p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{actionError}</span>
        </div>
      )}

      {/* Member Stats */}
      {memberSummary && (
        <div className="grid grid-cols-3 sm:grid-cols-3 gap-1 sm:gap-2">
          <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <div className="flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium text-purple-700 dark:text-purple-400">
                {memberSummary.admins} Admins
              </span>
            </div>
          </div>
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-center gap-1.5">
              <Edit3 className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                {memberSummary.members} Members
              </span>
            </div>
          </div>
          <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-1.5">
              <Eye className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-400">
                {memberSummary.viewers} Viewers
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Member List */}
      <div className="space-y-2">
        {projectMembers.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                {member.user.avatar ? (
                  <img
                    src={member.user.avatar}
                    alt={`${member.user.firstName} ${member.user.lastName}`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {member.user.firstName.charAt(0)}
                    {member.user.lastName.charAt(0)}
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {member.user.firstName} {member.user.lastName}
                  </span>
                  {member.isOwner && (
                    <span title={t('projects.members.owner')}>
                      <Crown className="w-4 h-4 text-yellow-500" />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {getRoleBadge(member.role)}
                  {member.user.email && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {member.user.email}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Project owners and org admins can manage members */}
            {!member.isOwner && canManage && (
              <div className="relative">
                <button
                  onClick={() => setMemberMenuOpen(memberMenuOpen === member.id ? null : member.id)}
                  className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {memberMenuOpen === member.id && (
                  <div className="absolute right-0 mt-1 w-48 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-10">
                    {/* Role Change Options */}
                    <>
                      <button
                        onClick={() => handleRoleChange(member.userId, 'ADMIN')}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        disabled={member.role === 'ADMIN'}
                      >
                        <Shield className="w-4 h-4 text-purple-500" />
                        Admin
                      </button>
                      <button
                        onClick={() => handleRoleChange(member.userId, 'MEMBER')}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        disabled={member.role === 'MEMBER'}
                      >
                        <Edit3 className="w-4 h-4 text-blue-500" />
                        Member
                      </button>
                      <button
                        onClick={() => handleRoleChange(member.userId, 'VIEWER')}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        disabled={member.role === 'VIEWER'}
                      >
                        <Eye className="w-4 h-4 text-gray-500" />
                        Viewer
                      </button>
                      <div className="border-t border-gray-200 dark:border-gray-700" />
                    </>
                    {/* Remove Option */}
                    <button
                      onClick={() => {
                        setMemberMenuOpen(null);
                        setShowRemoveModal(member);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('projects.members.remove')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {projectMembers.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No members yet</p>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('projects.members.addMember')}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Select User
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">Choose a user...</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
            {availableUsers.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                All users are already members of this project
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as ProjectRole)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="MEMBER">Member - Can create and edit tasks</option>
              <option value="ADMIN">Admin - Can manage members and settings</option>
              <option value="VIEWER">Viewer - Can only view tasks</option>
            </select>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={() => setShowAddModal(false)} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAddMember} isLoading={isLoading} className="w-full sm:w-auto">
              {t('projects.members.addMember')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove Member Confirmation */}
      <Modal
        isOpen={!!showRemoveModal}
        onClose={() => setShowRemoveModal(null)}
        title={t('projects.members.remove')}
        size="sm"
      >
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to remove{' '}
            <strong>
              {showRemoveModal?.user.firstName} {showRemoveModal?.user.lastName}
            </strong>{' '}
            from this project?
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <Button variant="secondary" onClick={() => setShowRemoveModal(null)} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleRemoveMember} className="w-full sm:w-auto">
              {t('projects.members.remove')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectMembersList;